/**
 * Post-Auth0 session flow: organisations → `my-access` filter → pick org
 * (lastActiveOrg hint) → `refresh-token` (atomic swap, mirrors
 * `Navbar.vue:2494-2570`) → `/user/permission`. Ported from
 * `Auth0Callback.vue` + `Navbar.vue` per the design spec Part C.
 */
import { API_BASE_URL } from "@/tenant/resolve";
import { unwrapCompressedBody } from "@/auth/compression";
import type {
  GetUserOrganisationsResponse,
  NavBarItem,
  RefreshTokenRequest,
  RefreshTokenResponse,
  TenantAppMyAccessResponse,
  UserOrganisation,
  UserPermissionResponse,
} from "@/tenant/types";

/**
 * Server-side truth: a 403 from `POST /user-access/refresh-token` means the
 * caller's organisation is not part of `tenantAppSlug` (revoked, or never
 * granted) — the shell treats this identically to the `my-access` empty-list
 * case (design addendum, binding). Distinguished from a generic `Error` so
 * the store can route to NoAccess instead of showing a bare failure message.
 */
export class TenantAccessDeniedError extends Error {
  constructor(message = "Not part of this app") {
    super(message);
    this.name = "TenantAccessDeniedError";
  }
}

/**
 * A non-2xx response, carrying its status.
 *
 * Added for DEV-1110: a device credential's token exchange has to tell "this
 * credential was revoked" (401 — stop, sign in again) from "there is no
 * network" (a thrown fetch — keep working from cache), and reading a status out
 * of a message string is the kind of thing that breaks silently when someone
 * improves the wording. Extends `Error`, so every existing `catch` is unchanged.
 */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** PAC-1387 — the server's `details.reason`, when it sent one. */
    readonly reason: string | null = null,
    /** The server's own `message`, without the "GET /path failed: 403" prefix. */
    readonly serverMessage: string | null = null,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

/**
 * PAC-1387 — the auth handler's sign-in refusals the shell has a screen for.
 * Mirrors `metaship-backend` `Services/Authentication/auth-refusals.ts`.
 */
export const AUTH_REFUSAL = {
  /** New Auth0 identity whose email is not proven yet → "Verify your email". */
  EMAIL_NOT_VERIFIED: "EMAIL_NOT_VERIFIED",
  /** Verified identity with no Metaship user yet → treated as "no organisation". */
  ACCOUNT_NOT_FOUND: "ACCOUNT_NOT_FOUND",
} as const;

export type AuthRefusal = (typeof AUTH_REFUSAL)[keyof typeof AUTH_REFUSAL];

/**
 * Which known refusal (if any) an error is. Reads `details.reason` first; the
 * status + wording fallback covers a backend that predates the reason field,
 * so the shell can ship ahead of — or behind — the backend safely.
 */
export function authRefusalOf(error: unknown): AuthRefusal | null {
  if (!(error instanceof ApiRequestError)) return null;
  if (error.reason === AUTH_REFUSAL.EMAIL_NOT_VERIFIED || error.reason === AUTH_REFUSAL.ACCOUNT_NOT_FOUND) {
    return error.reason;
  }
  const text = error.serverMessage ?? "";
  if (error.status === 403 && /verify your email/i.test(text)) return AUTH_REFUSAL.EMAIL_NOT_VERIFIED;
  if (error.status === 401 && /user not found in database/i.test(text)) return AUTH_REFUSAL.ACCOUNT_NOT_FOUND;
  return null;
}

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
  bearer?: string | null;
  orgToken?: string | null;
  /** Throw `TenantAccessDeniedError` (instead of a generic `Error`) on a 403 response. */
  accessDeniedOn403?: boolean;
}

/**
 * Exported for `@/device/deviceCredential` (DEV-1110) — the device-enrolment
 * calls are the same shape (same base URL, same Encore gzip envelope, same
 * bearer/apikey pair) and a second copy of this would be a second place for the
 * envelope handling to be forgotten.
 */
export async function apiRequest<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.bearer) headers.Authorization = opts.bearer;
  if (opts.orgToken) headers.apikey = opts.orgToken;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: opts.method || "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 403 && opts.accessDeniedOn403) {
    throw new TenantAccessDeniedError();
  }
  if (!res.ok) {
    const detail = await res.json().catch(() => null);
    const serverMessage = typeof detail?.message === "string" ? detail.message : null;
    const reason = typeof detail?.details?.reason === "string" ? detail.details.reason : null;
    throw new ApiRequestError(
      `${opts.method || "GET"} ${path} failed: ${res.status}${serverMessage ? " " + serverMessage : ""}`,
      res.status,
      reason,
      serverMessage,
    );
  }
  // Encore gzip envelope: any >1KB payload arrives as { result|data: "H4sIA…" }.
  return (await unwrapCompressedBody(await res.json())) as T;
}

/** `GET /user-access/organisation` — every org the caller belongs to (unfiltered by tenant). */
export async function fetchOrganisations(bearer: string): Promise<UserOrganisation[]> {
  const body = await apiRequest<GetUserOrganisationsResponse>("/user-access/organisation", { bearer });
  return body.result || [];
}

/**
 * `POST /portal/invites/redeem` — DEV-1044. Claims the organisation the invite
 * names. The org is taken from the invite ROW server-side; nothing in this body
 * can choose or influence which organisation the caller ends up in.
 */
export async function redeemInvite(
  token: string,
  bearer: string,
): Promise<{ organisationId: number; organisationName: string }> {
  return apiRequest("/portal/invites/redeem", { method: "POST", bearer, body: { token } });
}

/**
 * DEV-937 — `POST /boer/signup`.
 *
 * `auth: false` on the backend and verified against the Auth0 JWKS there, so
 * this sends the Auth0 bearer rather than an org token — the caller has no
 * organisation yet, which is the entire point.
 *
 * Deliberately sends NO business identity. The AgriOps route
 * (`POST /boer/agriops/signup`) is the only way a verified claim can be made,
 * because a client-supplied business id would let anyone claim any organisation
 * that has no members. This form creates a fresh, unverified organisation.
 */
export interface ProducerSignupResult {
  outcome: "CREATED" | "CLAIMED" | "NEEDS_REVIEW";
  organisationId: number | null;
  organisationName: string | null;
  /** Only on NEEDS_REVIEW — the grower quotes this to support. */
  reference: string | null;
}

export async function createProducerOrganisation(
  tenantAppSlug: string,
  bearer: string,
  farm: { farmName: string; contactName?: string; contactPhone?: string; province?: string },
): Promise<ProducerSignupResult> {
  return apiRequest<ProducerSignupResult>("/boer/signup", {
    method: "POST",
    bearer,
    body: { tenantAppSlug, ...farm },
  });
}

/**
 * PAC-1374 — the company sign-up form of a WMS front door (an app whose
 * resolve answer has `signupForm: "COMPANY"`). Same three outcomes as the farm
 * form. A registration/VAT number is a SEARCH key only — the backend never
 * claims an organisation on it alone (see Organisation/packhouse-signup-rules.ts).
 */
export interface CompanySignupFields {
  legalName: string;
  registrationNumber?: string;
  vatNumber?: string;
  /** ISO 4217. */
  baseCurrency: string;
  /** ISO 3166-1 alpha-2 — the company's legal home; each site's country comes from its POI. */
  countryCode: string;
  contactName?: string;
  contactPhone?: string;
}

export async function createCompanyOrganisation(
  tenantAppSlug: string,
  bearer: string,
  company: CompanySignupFields,
): Promise<ProducerSignupResult> {
  return apiRequest<ProducerSignupResult>("/packhouse/signup", {
    method: "POST",
    bearer,
    body: { tenantAppSlug, ...company },
  });
}

/**
 * PAC-1387 — `POST /auth0/verification-email/resend`. `auth: false` on the
 * backend, and the Auth0 id_token travels in the BODY with no Authorization
 * header: a header would make Encore run the platform auth handler, which
 * refuses an unverified identity (403 EMAIL_NOT_VERIFIED) before the resend
 * handler runs. The backend only ever mails the verified token's own subject.
 */
export interface ResendVerificationResult {
  status: "SENT" | "TOO_SOON" | "ALREADY_VERIFIED";
  retryAfterSeconds: number | null;
}

export async function resendVerificationEmail(bearer: string): Promise<ResendVerificationResult> {
  const idToken = bearer.replace(/^Bearer\s+/i, "").trim();
  return apiRequest<ResendVerificationResult>("/auth0/verification-email/resend", {
    method: "POST",
    body: { idToken },
  });
}

/** `GET /tenant-apps/my-access?slug=` — Appendix A3 (keyed by the resolved app's slug, not its hostname). */
export async function fetchMyAccess(slug: string, bearer: string): Promise<TenantAppMyAccessResponse> {
  return apiRequest<TenantAppMyAccessResponse>(`/tenant-apps/my-access?slug=${encodeURIComponent(slug)}`, { bearer });
}

/**
 * `POST /user-access/refresh-token` — mints the org-scoped Metaship token.
 * `tenantAppSlug` is required on EVERY call from this shell (initial sign-in,
 * org switch, proactive refresh) — the backend enforces the access gate on
 * this endpoint, not just on `my-access`; a 403 here throws
 * `TenantAccessDeniedError`.
 */
export async function mintOrgToken(
  org: UserOrganisation,
  bearer: string,
  tenantAppSlug: string,
): Promise<RefreshTokenResponse> {
  const requestBody: RefreshTokenRequest = { organisation: org.token, organisationName: org.name, tenantAppSlug };
  const body = await apiRequest<{ data: RefreshTokenResponse }>("/user-access/refresh-token", {
    method: "POST",
    bearer,
    accessDeniedOn403: true,
    body: requestBody,
  });
  return body.data;
}

/**
 * `GET /user/permission` — the NavBarList filtered to this user+org's access.
 *
 * The payload key is `data` (Services/User/user.ts), unlike its UserAccess
 * neighbours, which use `result`. Both are accepted: reading only `result`
 * silently yielded `[]` on every sign-in, which is what left every tenant
 * app's sidebar holding nothing but "Home".
 */
export async function fetchPermissions(bearer: string, orgToken: string): Promise<NavBarItem[]> {
  const body = await apiRequest<UserPermissionResponse>("/user/permission", { bearer, orgToken });
  return body.data || body.result || [];
}

/* ── Pure logic (unit-testable without a network) ────────────────────────── */

function lastActiveOrgKey(userId: number | string): string {
  return `metaship:lastActiveOrg:${userId}`;
}

export function readLastActiveOrgId(userId: number | string): string | null {
  try {
    return localStorage.getItem(lastActiveOrgKey(userId));
  } catch {
    return null;
  }
}

export function rememberLastActiveOrgId(userId: number | string, orgId: number): void {
  try {
    localStorage.setItem(lastActiveOrgKey(userId), String(orgId));
  } catch {
    // best-effort — a missed hint just falls back to "first usable org" next login
  }
}

function isUsable(org: UserOrganisation): boolean {
  return org.accountStatus !== "SUSPENDED" && org.accountStatus !== "BANNED";
}

/**
 * In-app org switcher default (Sidebar footer): prefer the remembered last-active org
 * when it's still usable, else the first usable org. Mirrors
 * `Navbar.vue:3120` `pickDefaultOrganisation` — always resolves to SOME org
 * when one exists, since the switcher only ever offers orgs the user may use.
 */
export function pickDefaultOrganisation(
  orgs: UserOrganisation[],
  lastActiveOrgId: string | number | null,
): UserOrganisation | null {
  if (lastActiveOrgId != null) {
    const hit = orgs.find((o) => String(o.id) === String(lastActiveOrgId) && isUsable(o));
    if (hit) return hit;
  }
  return orgs.find(isUsable) || null;
}

/** WHITE_LABEL → `[organisationId]` (0 or 1 row); PRODUCT → every accessible org, unfiltered order. */
export function resolveAccessibleOrganisations(
  allOrgs: UserOrganisation[],
  myAccess: TenantAppMyAccessResponse,
): UserOrganisation[] {
  if (!myAccess.found || myAccess.organisationIds.length === 0) return [];
  const allowed = new Set(myAccess.organisationIds);
  return allOrgs.filter((o) => allowed.has(o.id));
}

export type AccessGateDecision =
  | { mode: "none" }
  | { mode: "auto"; org: UserOrganisation }
  | { mode: "pick"; orgs: UserOrganisation[] };

/**
 * The sign-in gate decision: no accessible org → NoAccess; exactly one, or a
 * remembered last-active org among several → sign straight in; otherwise show
 * the org picker. Deliberately does NOT fall back to "first org" on ties (that
 * would silently drop a multi-org user into a random org) — that fallback is
 * `pickDefaultOrganisation`'s job, for the already-signed-in switcher only.
 */
export function decideAccessGate(
  accessibleOrgs: UserOrganisation[],
  lastActiveOrgId: string | number | null,
): AccessGateDecision {
  if (accessibleOrgs.length === 0) return { mode: "none" };
  if (accessibleOrgs.length === 1) return { mode: "auto", org: accessibleOrgs[0]! };
  if (lastActiveOrgId != null) {
    const hit = accessibleOrgs.find((o) => String(o.id) === String(lastActiveOrgId));
    if (hit) return { mode: "auto", org: hit };
  }
  return { mode: "pick", orgs: accessibleOrgs };
}
